import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Wrench, Shield, FileCheck, RefreshCw, Sparkles, Settings, HardHat, Plus, Pencil } from 'lucide-react';

interface Props {
    data: ContractInsightsData;
    onAddMaintenance?: () => void;
    onEditMaintenance?: (id: number) => void;
}

const getMaintenanceIcon = (type?: string) => {
    const normalized = type?.toLowerCase() || '';
    if (normalized.includes('insurance')) return <Shield className="h-4 w-4" />;
    if (normalized.includes('warranty')) return <FileCheck className="h-4 w-4" />;
    if (normalized.includes('conversion')) return <RefreshCw className="h-4 w-4" />;
    if (normalized.includes('refinement')) return <Sparkles className="h-4 w-4" />;
    if (normalized.includes('servicing')) return <Settings className="h-4 w-4" />;
    if (normalized.includes('refurbishment')) return <HardHat className="h-4 w-4" />;
    return <Wrench className="h-4 w-4" />;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

export default function ContractInsightMaintenance({ data, onAddMaintenance, onEditMaintenance }: Props) {
    const maintenanceEvents = (data.maintenances || []).sort((a, b) =>
        new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
    );

    const totalCost = maintenanceEvents.reduce((sum, m) => sum + m.amount, 0);
    const maintenanceCount = maintenanceEvents.length;
    const lastMaintenance = maintenanceEvents[0];
    const avgCost = maintenanceCount > 0 ? totalCost / maintenanceCount : 0;

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <StatCard
                    title="Total Spent"
                    value={<CurrencyDisplay amount={totalCost} />}
                    description="Total accumulated cost of all maintenance and repair services recorded for this vehicle."
                />
                <StatCard
                    title="Maintenance Count"
                    value={`${maintenanceCount} Records`}
                    description="Total number of individual maintenance service events logged for this vehicle."
                />
                <StatCard
                    title="Last Maintenance"
                    value={lastMaintenance ? format(new Date(lastMaintenance.started_at), 'MMM d, yyyy') : 'N/A'}
                    description="Date of the most recent maintenance service performed on this vehicle."
                />
                <StatCard
                    title="Avg. Cost"
                    value={<CurrencyDisplay amount={avgCost} />}
                    description="Average cost per maintenance service event."
                />
            </div>

            {/* Maintenance History */}
            <Card className="border border-border/40">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <CardTitle>
                        Maintenance History
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono">
                            {maintenanceCount} Records
                        </Badge>
                        {onAddMaintenance && (
                            <Button variant="outline" size="sm" onClick={onAddMaintenance} className="h-8">
                                <Plus className="h-4 w-4 mr-1" />
                                Add
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {maintenanceEvents.length > 0 ? (
                        <div className="space-y-4">
                            {maintenanceEvents.map((event) => (
                                <div key={event.id} className="group flex items-start justify-between gap-4 pb-4 border-b border-border/40 last:border-0 last:pb-0">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 h-10 w-10 rounded-full bg-muted flex items-center justify-center border border-border text-foreground">
                                            {getMaintenanceIcon(event.type)}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="font-medium text-sm">
                                                    {format(new Date(event.started_at), 'MMM d, yyyy')}
                                                </span>
                                                {event.type && (
                                                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 capitalize bg-background">
                                                        {event.type.toLowerCase()}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground line-clamp-2">
                                                {event.description || 'Maintenance service'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="font-semibold text-base">{formatCurrency(event.amount)}</span>
                                        {onEditMaintenance && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={() => onEditMaintenance(event.id)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-xl bg-muted/20">
                            <Wrench className="h-8 w-8 mx-auto mb-3 opacity-30" />
                            <p className="text-sm font-medium">No maintenance records</p>
                            <p className="text-xs text-muted-foreground mt-1">Add a maintenance record to track vehicle services</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
